import { QueryClient } from "react-query";
import { isEqual } from "lodash";

const CACHE_KEY = "foodpilot-query-cache";
const CACHE_VERSION = "v1";
const MAX_AGE = 1000 * 60 * 60 * 24 * 7; // 7 days

type CacheEntry = {
    state: {
        data: unknown;
        dataUpdatedAt: number;
    };
    version: string;
};

type PersistedCache = {
    timestamp: number;
    clientState: Record<string, CacheEntry>;
};

export const loadPersistedCache = (): PersistedCache | null => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;

        const parsed: PersistedCache = JSON.parse(cached);

        if (Date.now() - parsed.timestamp > MAX_AGE) {
            localStorage.removeItem(CACHE_KEY);
            return null;
        }

        return parsed;
    } catch (error) {
        return null;
    }
};

export const persistCache = (queryClient: QueryClient) => {
    try {
        const queries = queryClient.getQueryCache().getAll();
        const clientState: Record<string, CacheEntry> = {};

        queries.forEach((query) => {
            const queryKey = JSON.stringify(query.queryKey);
            if (query.state.data !== undefined) {
                clientState[queryKey] = {
                    state: {
                        data: query.state.data,
                        dataUpdatedAt: query.state.dataUpdatedAt,
                    },
                    version: CACHE_VERSION,
                };
            }
        });

        const persistedCache: PersistedCache = {
            timestamp: Date.now(),
            clientState,
        };

        localStorage.setItem(CACHE_KEY, JSON.stringify(persistedCache));
    } catch (error) {
        // Silently fail if localStorage is full or unavailable
    }
};

export const restoreCache = (queryClient: QueryClient) => {
    const persisted = loadPersistedCache();
    if (!persisted) return;

    Object.entries(persisted.clientState).forEach(([queryKeyStr, entry]) => {
        try {
            if (entry.version !== CACHE_VERSION) return;

            const queryKey = JSON.parse(queryKeyStr);
            queryClient.setQueryData(queryKey, entry.state.data);
        } catch (error) {
            // Skip invalid entries
        }
    });
};

export const clearPersistedCache = () => {
    try {
        localStorage.removeItem(CACHE_KEY);
    } catch (error) {
        // Silently fail if localStorage is unavailable
    }
};

export const shouldInvalidateCache = <T>(oldData: T | undefined, newData: T | undefined): boolean => {
    if (oldData === undefined && newData === undefined) return false;
    if (oldData === undefined || newData === undefined) return true;

    return !isEqual(oldData, newData);
};
